import { useEffect, useState } from "react";
import * as React from "react";
import { Button, Input, Form } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

import { DialogMessageType } from "../../types/commands";
import { log } from "../../utils/logger";

const escapeSheetName = (name: string) => name.replace(/'/g, "''");

type PlaceholderData = {
  placeholderName?: string;
};

const Placeholder: React.FC = () => {
  // Empty until the user selects something
  const [address, setAddress] = useState<string>("");

  const handleSubmit = () => {
    if (!address) return;

    Office.context.ui.messageParent(
      JSON.stringify({
        action: DialogMessageType.END_CELL_SELECTION,
        data: { address },
      })
    );
  };

  useEffect(() => {
    const handleMessage = (args: Office.DialogParentMessageReceivedEventArgs) => {
      try {
        const message = JSON.parse(args.message);

        if (message.action !== DialogMessageType.UPDATE_ADDRESS) return;

        const rawAddress: string | undefined = message.address;
        const sheetName: string | undefined = message.sheetName;

        if (!rawAddress || !sheetName) {
          log.error("UPDATE_ADDRESS missing address or sheetName:", message);
          return;
        }

        const qualifiedSheet = `'${escapeSheetName(sheetName)}'!`;

        // Preserve original range-vs-single-cell behavior based on placeholderName
        const placeholderRaw = localStorage.getItem("placeholder");
        let placeholderName: string | undefined;

        if (placeholderRaw) {
          try {
            const parsed: PlaceholderData = JSON.parse(placeholderRaw);
            placeholderName = parsed.placeholderName;
          } catch {
            // keep silent like original
          }
        }

        // INPUT_RANGE_CELL_REFERENCE: keep range as-is, qualify with sheet
        if (placeholderName === "INPUT_RANGE_CELL_REFERENCE") {
          setAddress(`${qualifiedSheet}${rawAddress}`);
          return;
        }

        // DATE_CELL_REFERENCE / CUSTOM...: single-cell only, qualify with sheet
        if (
          placeholderName === "DATE_CELL_REFERENCE" ||
          placeholderName === "CUSTOM_BUILT_INDEX_FORMULA_LOCATION_CELL_REFERENCE"
        ) {
          const singleCell = rawAddress.includes(":") ? rawAddress.split(":")[0] : rawAddress;
          setAddress(`${qualifiedSheet}${singleCell}`);
          return;
        }

        // Default: collapse range to first cell, but ALWAYS qualify with sheet
        const singleCell = rawAddress.includes(":") ? rawAddress.split(":")[0] : rawAddress;
        setAddress(`${qualifiedSheet}${singleCell}`);
      } catch (error) {
        log.error("Error parsing message:", error);
      }
    };

    // Start selection mode in the parent
    Office.context.ui.messageParent(
      JSON.stringify({ action: DialogMessageType.START_CELL_SELECTION })
    );

    // Register handler
    Office.context.ui.addHandlerAsync(
      Office.EventType.DialogParentMessageReceived,
      handleMessage
    );

    // Optional cleanup (safe for older typings/hosts)
    return () => {
      const uiAny = Office.context.ui as any;
      if (typeof uiAny.removeHandlerAsync === "function") {
        uiAny.removeHandlerAsync(
          Office.EventType.DialogParentMessageReceived,
          { handler: handleMessage }
        );
      }
    };
  }, []);

  return (
    <Form
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 20px",
        gap: "10px",
      }}
      onFinish={handleSubmit}
    >
      <Form.Item style={{ flexGrow: 1 }}>
        <Input
          placeholder="Select a cell…"
          value={address ? `=${address}` : ""}
          style={{ width: "100%" }}
          readOnly
          autoFocus
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          icon={<DownloadOutlined />}
          disabled={!address}
        />
      </Form.Item>
    </Form>
  );
};

export default Placeholder;
